import { useMemo, useState } from "react";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Heading,
	Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
} from "@chakra-ui/react";
import { useHasNotificationsConfiguration } from "hooks/featureFlags";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { BlockedRemovalModal } from "../components/BlockedRemovalModal";
import { CoverageBanner } from "../components/CoverageBanner";
import { ParticipantPanelErrorBoundary } from "../components/ParticipantPanelErrorBoundary";
import { RuleEditor } from "../components/RuleEditor";
import { resolveNotificationLangKey } from "../constants/lang";
import {
	asCoverageViolationResponse,
	useRfxNotificationMgmt,
	useRfxNotificationRulesMutation,
} from "../hooks/useRfxNotificationMgmt";
import type {
	CoverageViolationDetail,
	RfxNotificationParticipant,
	RfxNotificationRuleWritePayload,
} from "../types/rfx-notification-mgmt.types";

const isAxios403 = (error: unknown): boolean => {
	const status = (error as { response?: { status?: number } })?.response?.status;
	return status === 403;
};

const participantKey = (participant: RfxNotificationParticipant): string =>
	`${participant.user_id ?? "guest"}-${participant.guest_invite_id ?? "none"}-${participant.email}`;

interface EffectiveChannelSetting {
	delivery: string;
	source: string;
	locked: boolean;
	lock_origin?: string | null;
}

const isEffectiveChannelSetting = (value: unknown): value is EffectiveChannelSetting =>
	typeof value === "object" &&
	value !== null &&
	"delivery" in value &&
	"source" in value;

// The live payload's `effective` is nested — one entry per notification type,
// each holding a per-channel (mail/in_app) delivery setting — not a flat
// type -> string map. Render each channel's delivery/locked state instead of
// falling back to `String(value)` (which just showed "[object Object]").
const describeEffectiveEntry = (value: unknown): string => {
	if (typeof value !== "object" || value === null) {
		return String(value);
	}

	return Object.entries(value as Record<string, unknown>)
		.map(([channel, channelValue]) => {
			if (!isEffectiveChannelSetting(channelValue)) {
				return `${channel}: ${String(channelValue)}`;
			}
			const lockSuffix = channelValue.locked ? " (locked)" : "";
			return `${channel}: ${channelValue.delivery}${lockSuffix}`;
		})
		.join(", ");
};

// Removal is only "blocked" (VT-013, newui Modal — never window.confirm) when the
// server-provided coverage row for this participant's company/type shows exactly
// one remaining recipient. This reads server-computed fields as-is; it never
// re-derives coverage math client-side (learnings/frontend/2026-06-30).
const isBlockedRemoval = (
	participant: RfxNotificationParticipant,
	typeKey: string,
	rule: RfxNotificationRuleWritePayload["rule"],
	coverage: { company_id: number; type_key: string; recipient_count: number }[]
): boolean => {
	if (participant.side !== "bidder") {
		return false;
	}
	if (rule !== "lock_off" && rule !== "deny_all") {
		return false;
	}
	return coverage.some(
		(row) => row.type_key === typeKey && row.recipient_count <= 1
	);
};

export const RfxNotificationMgmtPage = () => {
	const hasFlag = useHasNotificationsConfiguration();
	const { rfxId } = useRfxData() ?? {};
	const notificationMgmtQuery = useRfxNotificationMgmt(rfxId ?? 0);
	const rulesMutation = useRfxNotificationRulesMutation(rfxId ?? 0);

	const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
	const [coverageViolations, setCoverageViolations] = useState<
		CoverageViolationDetail[]
	>([]);
	const [blockedRemoval, setBlockedRemoval] = useState<{
		companyName: string;
	} | null>(null);

	const typeKeys = useMemo(() => {
		const data = notificationMgmtQuery.data;
		if (!data) {
			return [];
		}
		const keys = new Set<string>();
		// Defensive: a null/undefined type_key (e.g. a rule pointing at a
		// since-deleted notification type) must never produce an editable
		// row keyed on a non-string value — skip it here rather than crash
		// or render a broken RuleEditor.
		data.coverage.forEach((row) => {
			if (row.type_key) {
				keys.add(row.type_key);
			}
		});
		data.rules.forEach((rule) => {
			if (rule.type_key) {
				keys.add(rule.type_key);
			}
		});
		return Array.from(keys);
	}, [notificationMgmtQuery.data]);

	if (!hasFlag) {
		return null;
	}

	if (isAxios403(notificationMgmtQuery.error)) {
		return (
			<Box data-testid="notification-mgmt-access-denied" p={8} textAlign="center">
				<Heading size="md">Access denied</Heading>
				<Text mt={2}>You do not have permission to manage notifications for this RFx.</Text>
			</Box>
		);
	}

	if (notificationMgmtQuery.isLoading || !notificationMgmtQuery.data) {
		return <Spinner data-testid="notification-mgmt-loading" />;
	}

	const { participants, rules, coverage } = notificationMgmtQuery.data;

	const rulesForParticipant = (participant: RfxNotificationParticipant) =>
		rules.filter(
			(rule) =>
				rule.target_user_id !== null && rule.target_user_id === participant.user_id
		);

	const handleRuleSave = (
		participant: RfxNotificationParticipant,
		payload: RfxNotificationRuleWritePayload
	) => {
		if (isBlockedRemoval(participant, payload.type_key, payload.rule, coverage)) {
			setBlockedRemoval({ companyName: participant.company });
			return;
		}

		setInlineErrors((prev) => {
			const next = { ...prev };
			delete next[payload.type_key];
			return next;
		});
		setCoverageViolations([]);

		rulesMutation.mutate(
			{ rules: [payload] },
			{
				onError: (error) => {
					const violation = asCoverageViolationResponse(error);
					if (!violation) {
						return;
					}

					if (violation.status === 422) {
						// AC4/VT-014: static forbid — surface inline on the offending row.
						setInlineErrors((prev) => ({
							...prev,
							[payload.type_key]: resolveNotificationLangKey(violation.body.message),
						}));
						return;
					}

					// AC2/VT-012: dynamic reduce — CoverageBanner warning + block save.
					setCoverageViolations(violation.body.violations);
				},
			}
		);
	};

	return (
		<Box data-testid="rfx-notification-mgmt-page">
			<Heading size="lg">Notification management</Heading>

			<CoverageBanner violations={coverageViolations} />

			<Accordion allowToggle data-testid="participant-accordion">
				{participants.map((participant) => (
					<AccordionItem key={participantKey(participant)}>
						{({ isExpanded }) => (
							<>
								<AccordionButton data-testid={`participant-row-${participantKey(participant)}`}>
									<Box flex="1" textAlign="left">
										<Text fontWeight="bold">{participant.name}</Text>
										<Text fontSize="sm" color="gray.500">
											{participant.company} · {participant.side} · {participant.email}
										</Text>
									</Box>
									<AccordionIcon />
								</AccordionButton>
								<AccordionPanel>
									{/* VT-010 perf/crash fix: Chakra's Accordion mounts every
									AccordionPanel up-front by default. With ~19 participants
									each rendering a ~65-row effective table plus several
									RuleEditors (react-select instances), that's a very heavy
									simultaneous mount. Only render panel content once this
									item is actually expanded. */}
									{isExpanded ? (
										<ParticipantPanelErrorBoundary participantName={participant.name}>
											<Table size="sm" data-testid="effective-settings-table">
												<Thead>
													<Tr>
														<Th>Type</Th>
														<Th>Effective setting</Th>
													</Tr>
												</Thead>
												<Tbody>
													{/* Defensive: some participants (guests) legitimately
													have effective: {} — and a malformed response could
													send null/undefined instead. Object.entries on
													null/undefined throws, so guard with an empty-object
													fallback. */}
													{Object.entries(participant.effective ?? {}).map(([key, value]) => (
														<Tr key={key}>
															<Td>{key}</Td>
															<Td>{describeEffectiveEntry(value)}</Td>
														</Tr>
													))}
												</Tbody>
											</Table>

											{rulesForParticipant(participant).map((rule) => (
												<Text key={rule.id} fontSize="sm" data-testid={`existing-rule-${rule.id}`}>
													{/* Defensive: a null/undefined type_key (deleted
													notification type, or a malformed response) must
													render a safe fallback here rather than crash the
													page. */}
													{rule.type_key ?? "—"}: {rule.rule}
													{rule.reason ? ` — ${rule.reason}` : ""}
												</Text>
											))}

											{typeKeys.map((typeKey) => (
												<RuleEditor
													key={typeKey}
													typeKey={typeKey}
													targetUserId={participant.user_id}
													inlineError={inlineErrors[typeKey] ?? null}
													isSaving={rulesMutation.isPending}
													onSave={(payload) => handleRuleSave(participant, payload)}
												/>
											))}
										</ParticipantPanelErrorBoundary>
									) : null}
								</AccordionPanel>
							</>
						)}
					</AccordionItem>
				))}
			</Accordion>

			{blockedRemoval ? (
				<BlockedRemovalModal
					isOpen
					companyName={blockedRemoval.companyName}
					onClose={() => setBlockedRemoval(null)}
				/>
			) : null}
		</Box>
	);
};
